import {type Autocomplete} from '@ezcater/tapas';
import {type IconDefinition} from '@fortawesome/pro-regular-svg-icons';
import {type FieldHelperProps} from 'formik';

import {type ValidateAddressFunction} from '@/components/global/layout/AppBar';
import {type GeocodedAddressResult} from '@/utils/googleAutocomplete';

export const MANUAL_ENTRY_OPTION_ID = '__manual_entry__';

type PropsWithDataAttributes = Record<`data-${string}`, unknown>;
export type AutocompletePrediction = google.maps.places.AutocompletePrediction;

export type GetPlacePredictionsArgs = Parameters<
  typeof google.maps.places.AutocompleteService.prototype.getPlacePredictions
>;

export type AutocompleteOption = {
  description: string;
  group: string;
  place_id: string;
  structured_formatting: {
    main_text_matched_substrings?: AutocompletePrediction['structured_formatting']['main_text_matched_substrings'];
    main_text: string;
    secondary_text: string;
  };
  corporate?: boolean;
};

export type AutocompleteOptionGroup = {
  id: 'saved' | 'search' | 'manual';
  label: string;
  items: AutocompleteOption[];
};

export type AddressSearchAutocompleteOptionProps = React.ComponentPropsWithoutRef<'div'> & {
  isSavedAddress?: boolean;
  option: AutocompleteOption;
};

export type AddressSearchAutocompleteSuffixProps = {
  geocodedAddressResultHelpers: FieldHelperProps<GeocodedAddressResult | null>;
  inputHelpers: FieldHelperProps<string>;
  icon?: IconDefinition;
};

type ContentProps = React.ComponentPropsWithoutRef<typeof Autocomplete.Content>;

export type AddressSearchAutocompleteProps = {
  appBarStepThrough?: boolean;
  disabled?: boolean;
  /** Shows the error border and marks the input `aria-invalid` */
  error?: boolean;
  /** The field name to store the selected Geocoded Address in the formik form */
  geocodedAddressResultName: string;
  /** The field name to store the selected Google Place in the formik form */
  googlePlaceName: string;
  /** Lands on the `<input>`, so a `<label htmlFor>` can target it */
  id: string;
  inputName: string;
  corpAccountFilterId?: string;
  shouldIncludeSavedAddresses?: boolean;
  shouldSubmitOnSelect?: boolean;
  onInputChange?: (inputValue?: string) => void;
  /** Controls the popup; it still only shows while there are options */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Replaces the default map-marker icon; `null` hides it */
  prefix?: React.ReactNode;
  /** The field name to store the selected Saved Address in the formik form */
  savedAddressName?: string;
  showManualEntryOption?: boolean;
  onManualEntrySelected?: () => void;
  /** Shows the "use my location" button in the input suffix */
  showLocationButton?: boolean;
  slotProps?: {
    inputGroup?: {className?: string};
    input?: Omit<React.ComponentPropsWithoutRef<typeof Autocomplete.Input>, 'className'> &
      PropsWithDataAttributes & {className?: string};
    content?: Pick<ContentProps, 'anchor' | 'sideOffset'> & {className?: string};
    item?: {className?: string};
  };
  trackingSubcategory?: 'navigation bar' | 'address search';
  validateAddress?: ValidateAddressFunction;
  experimentVariation?: 'treatment_red_dot' | 'treatment_text_prompt' | undefined;
};
